import { getLocale } from '@borealis/aurora';
import { useLingui } from '@lingui/react';
import '@warp-ds/components/icon';
import { Button } from '@warp-ds/react';
import { useEffect, useRef, useState } from 'react';

enum State {
  none = 'none',
  open = 'open',
  closed = 'closed',
}

type Props = {
  body: React.ReactNode;
  showLessText?: string;
  readMoreText: string;
};

export const ExpandableSection = ({ body, showLessText, readMoreText }: Props) => {
  const { i18n } = useLingui();
  const [expandedState, setExpandedState] = useState<State>(State.none);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [elementTopPosition, setElementTopPosition] = useState<number | null>(null);
  const areaRef = useRef<HTMLDivElement>(null);

  const showLessLabel =
    showLessText ??
    i18n.t({
      id: 'mobility-item-insertion.form.showLess',
      message: 'Show less',
      comment: 'Generic text for collapsing an expanded text section',
    });

  const initExpandableState = () => {
    if (areaRef.current) {
      const { clientHeight, scrollHeight } = areaRef.current;
      if (scrollHeight > clientHeight) {
        setExpandedState(State.closed);
      }
    }
  };

  useEffect(() => initExpandableState(), [body]);

  const toggleExpandedState = () => {
    if (expandedState !== State.open) {
      const absoluteTop = areaRef.current?.offsetTop ?? 0;
      setElementTopPosition(absoluteTop);
      setScrollPosition(window.scrollY);
    } else {
      const currentTop = areaRef.current?.offsetTop ?? 0;
      const scrollAdjustment = currentTop - (elementTopPosition || 0);
      window.scrollTo(0, scrollPosition + scrollAdjustment);
    }
    setExpandedState((prev) => (prev === State.open ? State.closed : State.open));
  };

  return (
    <section>
      <div
        data-testid="expandable-section"
        ref={areaRef}
        className={`overflow-hidden relative mt-16 ${expandedState === State.open ? 'max-h-none' : 'max-h-[240px]'}`}
      >
        <div className="whitespace-pre-wrap children:list-disc children:list-outside mb-8">{body}</div>

        {expandedState === State.closed && (
          <span
            className="absolute bottom-0 right-0 left-0 h-[100px]"
            style={{ background: 'linear-gradient(to top, var(--w-s-color-background), transparent)' }}
          />
        )}
      </div>

      {expandedState !== State.none && (
        <div className="py-6">
          <Button
            quiet
            className="flex items-center mt-24 !px-16"
            onClick={toggleExpandedState}
            data-testid="toggle-description"
            data-preview-dont-disable="true"
          >
            {expandedState === State.open ? (
              <>
                <w-icon name="Minus" size="small" locale={getLocale()} />
                <span className="px-8">{showLessLabel}</span>
              </>
            ) : (
              <>
                <w-icon name="Plus" size="small" locale={getLocale()} />
                <span className="px-8">{readMoreText}</span>
              </>
            )}
          </Button>
        </div>
      )}
    </section>
  );
};
